import { useEffect, useMemo, useState } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  LinearProgress,
  Menu,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { useAtom } from "jotai";
import { useSearchParams } from "react-router-dom";
import { AxiosError, HttpStatusCode } from "axios";
import moment, { Moment } from "moment";
import {
  useHasPermission,
  UserPermission,
} from "@zdistancelab-packages/ui-permission-validator";
import { queryClient } from "../../App";
import { Button, ConfirmationModal } from "../common";
import {
  selectedOrgIdAtom,
  toastOptionsAtom,
  userAtom,
  userPermissionAtom,
} from "../../atoms";
import {
  useCreateJobMutation,
  useDeleteJobMutation,
  useFetchAccountsQuery,
  useFetchJobsQuery,
  useFetchOrganizationInfoQuery,
  useFetchOrganizationsQuery,
  useFetchTrucksQuery,
  useUpdateJobMutation,
} from "../../api";
import { useFetchAllUsersQuery } from "../../api/userService";
import { JobModel, OrganizationModel, UserModel } from "../../models";
import {
  CALENDAR_CREW_ROLES,
  CALENDAR_DATE_FORMAT,
  CALENDAR_DEFAULT_START_TIME,
  CALENDAR_DEFAULT_TIMEZONE,
  CALENDAR_NO_ACCESS_MESSAGE,
  RECURRENCE_EDIT_SCOPE_OPTIONS,
  RECURRENCE_ENTIRE_SERIES_UNSUPPORTED_MESSAGE,
  RECURRENCE_SCOPE_OPTIONS,
} from "../../constants";
import {
  Action,
  CalendarLayer,
  CrewStatus,
  Module,
  RecurrenceEditScope,
  Role,
} from "../../enums";
import {
  ApiError,
  CalendarSlot,
  DropdownItem,
  Job,
  JobScheduleChange,
} from "../../types";
import {
  getApiErrorMessage,
  getZonedDateRangeUtc,
  getZonedToday,
  theme,
} from "../../utils";
import {
  CalendarSummaryCards,
  CalendarToolbar,
  JobDrawer,
  JobsWeekView,
  RecurrenceScopeDialog,
  ResourceWeekView,
} from "./calendar/fragments";

export const SprayzTime = () => {
  const [user] = useAtom(userAtom);
  const [queryParams] = useSearchParams();
  const [selectedOrgId, setSelectedOrgId] = useAtom(selectedOrgIdAtom);
  const [userPermission] = useAtom<UserPermission>(userPermissionAtom);
  const [, setToastOptions] = useAtom(toastOptionsAtom);
  const [weekStart, setWeekStart] = useState<Moment>(
    moment().startOf("isoWeek"),
  );
  const [selectedDay, setSelectedDay] = useState<Moment>();
  const [layer, setLayer] = useState(CalendarLayer.Jobs);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState<Job>();
  const [selectedSlot, setSelectedSlot] = useState<CalendarSlot>();
  const [pendingJob, setPendingJob] = useState<Job>();
  const [jobToDelete, setJobToDelete] = useState<Job>();
  const [conflictMenuAnchor, setConflictMenuAnchor] = useState<HTMLElement>();
  const [organizationsDropdown, setOrganizationsDropdown] = useState<
    DropdownItem[]
  >([]);
  const isSuperAdmin = user?.roleCode === Role.SuperAdmin;
  const isOrgSelected = selectedOrgId ? true : false;
  const canRead = useHasPermission(userPermission, [
    { module: Module.Job, action: Action.Read },
  ]);
  const canWrite = useHasPermission(userPermission, [
    { module: Module.Job, action: Action.Write },
  ]);
  const canDelete = useHasPermission(userPermission, [
    { module: Module.Job, action: Action.Delete },
  ]);
  const fetchOrganizationsQuery = useFetchOrganizationsQuery({
    enabled: isSuperAdmin,
  });
  const fetchOrganizationInfoQuery = useFetchOrganizationInfoQuery(
    selectedOrgId,
    { enabled: isOrgSelected },
  );
  const timeZone =
    fetchOrganizationInfoQuery.data?.timezone || CALENDAR_DEFAULT_TIMEZONE;
  const today = getZonedToday(timeZone);
  const days = useMemo(
    () => (selectedDay ? [selectedDay] : JobModel.buildWeekDays(weekStart)),
    [weekStart, selectedDay],
  );
  const range = useMemo(
    () =>
      getZonedDateRangeUtc(
        days[0].format(CALENDAR_DATE_FORMAT),
        days[days.length - 1].format(CALENDAR_DATE_FORMAT),
        timeZone,
      ),
    [days, timeZone],
  );
  const fetchJobsQuery = useFetchJobsQuery(
    selectedOrgId,
    range.startDate,
    range.endDate,
    {
      enabled:
        isOrgSelected && canRead && !fetchOrganizationInfoQuery.isLoading,
    },
  );
  const fetchAccountsQuery = useFetchAccountsQuery(selectedOrgId, {
    enabled: isOrgSelected,
  });
  const fetchTrucksQuery = useFetchTrucksQuery(selectedOrgId, {
    enabled: isOrgSelected,
  });
  const fetchAllUsersQuery = useFetchAllUsersQuery(selectedOrgId, {
    enabled: isOrgSelected,
  });
  const createJobMutation = useCreateJobMutation();
  const updateJobMutation = useUpdateJobMutation();
  const deleteJobMutation = useDeleteJobMutation();
  const isSaving =
    createJobMutation.isLoading ||
    updateJobMutation.isLoading ||
    deleteJobMutation.isLoading;

  const jobs = useMemo(
    () => JobModel.buildJobs(fetchJobsQuery.data ?? [], timeZone),
    [fetchJobsQuery.data, timeZone],
  );
  const accounts = useMemo(
    () => fetchAccountsQuery.data ?? [],
    [fetchAccountsQuery.data],
  );
  const trucks = useMemo(
    () => fetchTrucksQuery.data ?? [],
    [fetchTrucksQuery.data],
  );
  const crew = useMemo(
    () =>
      UserModel.buildCrewMembers(fetchAllUsersQuery.data ?? []).filter(
        (member) =>
          CALENDAR_CREW_ROLES.includes(member.roleCode as Role) &&
          member.status !== CrewStatus.Inactive,
      ),
    [fetchAllUsersQuery.data],
  );
  const accountDropdown = useMemo(
    () => JobModel.buildAccountDropdown(accounts),
    [accounts],
  );

  const conflictJobIds = useMemo(
    () =>
      new Set(
        jobs
          .filter(
            (job) =>
              JobModel.getJobConflicts(job, jobs, trucks, crew).length > 0,
          )
          .map((job) => job.id),
      ),
    [jobs, trucks, crew],
  );

  const conflictJobs = useMemo(
    () =>
      jobs
        .filter((job) => conflictJobIds.has(job.id))
        .sort(
          (a, b) =>
            a.date.localeCompare(b.date) ||
            JobModel.timeToMinutes(a.startTime) -
              JobModel.timeToMinutes(b.startTime),
        ),
    [jobs, conflictJobIds],
  );

  const truckRows = useMemo(
    () => JobModel.buildTruckRows(trucks, jobs, days),
    [trucks, jobs, days],
  );

  const crewRows = useMemo(
    () => JobModel.buildCrewRows(crew, jobs, days),
    [crew, jobs, days],
  );

  useEffect(() => {
    setWeekStart(moment(getZonedToday(timeZone)).startOf("isoWeek"));
  }, [timeZone]);

  useEffect(() => {
    if (fetchOrganizationsQuery.isSuccess) {
      setOrganizationsDropdown(
        OrganizationModel.buildOrganizationDropdown(
          fetchOrganizationsQuery.data,
        ),
      );
    } else if (fetchOrganizationsQuery.isError) {
      setOrganizationsDropdown([]);
    }
  }, [
    fetchOrganizationsQuery.isSuccess,
    fetchOrganizationsQuery.isError,
    fetchOrganizationsQuery.data,
  ]);

  useEffect(() => {
    if (organizationsDropdown.length > 0 && !selectedOrgId) {
      setSelectedOrgId(
        Number(queryParams.get("orgId")) ||
          user?.orgId ||
          (organizationsDropdown[0].value as number),
      );
    }
  }, [organizationsDropdown, selectedOrgId, setSelectedOrgId]);

  useEffect(() => {
    if (fetchJobsQuery.isError) {
      setToastOptions({
        open: true,
        message: getApiErrorMessage(
          fetchJobsQuery.error as AxiosError<ApiError>,
        ),
        severity: "error",
      });
    }
  }, [fetchJobsQuery.isError, fetchJobsQuery.error]);

  const getDayStartSlot = (date: Moment): CalendarSlot => ({
    date,
    startTime: CALENDAR_DEFAULT_START_TIME,
  });

  const handleWeekView = () => {
    if (selectedDay) setWeekStart(selectedDay.clone().startOf("isoWeek"));
    setSelectedDay(undefined);
  };

  const handleOpenDrawer = (job?: Job, slot?: CalendarSlot) => {
    if (!job && !canWrite) return;

    setSelectedJob(job);
    setSelectedSlot(slot ?? getDayStartSlot(moment(today)));
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
  };

  const handleConflictsClick = (anchor: HTMLElement) => {
    if (conflictJobs.length === 1) {
      handleOpenDrawer(conflictJobs[0]);
    } else {
      setConflictMenuAnchor(anchor);
    }
  };

  const handleJobSaved = (message: string) => {
    setIsDrawerOpen(false);
    queryClient.invalidateQueries(["fetchJobs", selectedOrgId]);
    setToastOptions({ open: true, message, severity: "success" });
  };

  const handleJobError = (error: AxiosError<ApiError>) => {
    if (error.response?.status === HttpStatusCode.NotFound) {
      setIsDrawerOpen(false);
      fetchJobsQuery.refetch();
    }

    setToastOptions({
      open: true,
      message: getApiErrorMessage(error),
      severity: "error",
    });
  };

  const updateJob = (job: Job, scope: RecurrenceEditScope) => {
    if (scope === RecurrenceEditScope.EntireSeries) {
      setToastOptions({
        open: true,
        message: RECURRENCE_ENTIRE_SERIES_UNSUPPORTED_MESSAGE,
        severity: "warning",
      });
      return;
    }

    updateJobMutation.mutate(
      {
        orgId: selectedOrgId,
        id: job.id,
        ...JobModel.buildUpdateJobPayload(job, timeZone, scope),
      },
      {
        onSuccess: () => {
          setPendingJob(undefined);
          handleJobSaved(
            `${JobModel.getCategoryLabel(job)} updated successfully`,
          );
        },
        onError: (error) => {
          setPendingJob(undefined);
          handleJobError(error);
        },
      },
    );
  };

  const handleSaveJob = (job: Job) => {
    if (!selectedJob) {
      createJobMutation.mutate(
        {
          orgId: selectedOrgId,
          ...JobModel.buildCreateJobPayload(job, timeZone),
        },
        {
          onSuccess: () =>
            handleJobSaved(
              `${JobModel.getCategoryLabel(job)} created successfully`,
            ),
          onError: handleJobError,
        },
      );
    } else if (selectedJob.seriesId !== null) {
      setPendingJob(job);
    } else {
      updateJob(job, RecurrenceEditScope.ThisOccurrence);
    }
  };

  const handleMoveJob = (job: Job, changes: JobScheduleChange) => {
    const movedJob = { ...job, ...changes };

    if (job.seriesId !== null) {
      setPendingJob(movedJob);
    } else {
      updateJob(movedJob, RecurrenceEditScope.ThisOccurrence);
    }
  };

  const deleteJob = (scope: RecurrenceEditScope) => {
    if (!jobToDelete) return;

    deleteJobMutation.mutate(
      { orgId: selectedOrgId, id: jobToDelete.id, scope },
      {
        onSuccess: () => {
          setJobToDelete(undefined);
          handleJobSaved(
            `${JobModel.getCategoryLabel(jobToDelete)} deleted successfully`,
          );
        },
        onError: (error) => {
          setJobToDelete(undefined);
          handleJobError(error);
        },
      },
    );
  };

  if (!canRead) {
    return (
      <Box className="dashboard-page">
        <Box className="dashboard-header">
          <Typography className="dashboard-title">
            SprayzTime Calendar
          </Typography>
        </Box>
        <Box className="dashboard-card" sx={{ p: 3 }}>
          <Typography className="dashboard-helper-text">
            {CALENDAR_NO_ACCESS_MESSAGE}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className="dashboard-page">
      <Box className="dashboard-header">
        <Box sx={{ minWidth: 0 }}>
          <Typography className="dashboard-title">
            SprayzTime Calendar
          </Typography>
          <Typography className="dashboard-subtitle">
            Schedule jobs, trucks and crew
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
            width: { xs: "100%", md: "auto" },
          }}
        >
          {isSuperAdmin && (
            <FormControl
              size="small"
              sx={{
                minWidth: { xs: "100%", sm: 220 },
                maxWidth: { xs: "100%", sm: 320 },
                backgroundColor: "var(--dashboard-surface)",
                borderRadius: "var(--dashboard-radius-control)",
              }}
            >
              <InputLabel>Organization</InputLabel>
              <Select
                value={organizationsDropdown.length ? selectedOrgId : ""}
                label="Organization"
                onChange={(event) =>
                  setSelectedOrgId(Number(event.target.value))
                }
                sx={{
                  height: 42,
                  borderRadius: "var(--dashboard-radius-control)",
                }}
              >
                {organizationsDropdown.map((organization) => (
                  <MenuItem key={organization.value} value={organization.value}>
                    {organization.key}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {canWrite && (
            <Button
              label="Schedule"
              variantType="primary"
              icon={AddIcon}
              onClick={() => handleOpenDrawer()}
              sx={{
                height: 42,
                px: 2.5,
                whiteSpace: "nowrap",
                width: { xs: "100%", sm: "auto" },
              }}
            />
          )}
        </Box>
      </Box>

      <CalendarToolbar
        weekStart={weekStart}
        selectedDay={selectedDay}
        layer={layer}
        onLayerChange={setLayer}
        onPrevious={() =>
          selectedDay
            ? setSelectedDay(selectedDay.clone().subtract(1, "day"))
            : setWeekStart((prev) => prev.clone().subtract(1, "week"))
        }
        onNext={() =>
          selectedDay
            ? setSelectedDay(selectedDay.clone().add(1, "day"))
            : setWeekStart((prev) => prev.clone().add(1, "week"))
        }
        onToday={() => {
          setWeekStart(moment(today).startOf("isoWeek"));
          if (selectedDay) setSelectedDay(moment(today));
        }}
        onDaySelect={setSelectedDay}
        onWeekView={handleWeekView}
      />

      <CalendarSummaryCards
        jobs={jobs}
        trucks={trucks}
        crew={crew}
        conflictCount={conflictJobIds.size}
        isDayView={!!selectedDay}
        onConflictsClick={handleConflictsClick}
      />

      <Menu
        open={!!conflictMenuAnchor}
        anchorEl={conflictMenuAnchor}
        onClose={() => setConflictMenuAnchor(undefined)}
        disableAutoFocusItem
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ mt: 3 }}
        MenuListProps={{ sx: { py: 0 } }}
        slotProps={{
          paper: {
            className: "dashboard-card",
            sx: {
              width: { xs: conflictMenuAnchor?.offsetWidth, sm: 340 },
              maxHeight: { xs: "60vh", sm: 480 },
              border: "1px solid var(--dashboard-border-tile)",
            },
          },
        }}
      >
        {conflictJobs.map((job) => (
          <MenuItem
            key={job.id}
            onClick={() => {
              setConflictMenuAnchor(undefined);
              handleOpenDrawer(job);
            }}
            sx={{
              display: "block",
              whiteSpace: "normal",
              px: { xs: 2, sm: 2.5 },
              py: 1.5,
              "&:not(:last-of-type)": {
                borderBottom: "1px solid var(--dashboard-border-row)",
              },
              "&:hover, &.Mui-focusVisible": {
                backgroundColor: "var(--dashboard-background)",
              },
            }}
          >
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
              {JobModel.getJobTitle(job)}
            </Typography>
            <Typography className="dashboard-helper-text" sx={{ mt: 0.25 }}>
              {moment(job.date, CALENDAR_DATE_FORMAT).format("ddd, MMM D")} ·{" "}
              {JobModel.getTimeRangeLabel(job)}
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                mt: 1,
              }}
            >
              {JobModel.getJobConflicts(job, jobs, trucks, crew).map(
                (conflict, index) => (
                  <Typography
                    key={`${conflict}-${index}`}
                    sx={{
                      fontSize: 12,
                      lineHeight: 1.5,
                      color: "var(--dashboard-danger)",
                    }}
                  >
                    • {conflict}
                  </Typography>
                ),
              )}
            </Box>
          </MenuItem>
        ))}
      </Menu>

      <Box className="dashboard-card" sx={{ overflow: "hidden" }}>
        {fetchJobsQuery.isFetching && <LinearProgress />}
        {layer === CalendarLayer.Jobs && (
          <JobsWeekView
            days={days}
            today={today}
            jobs={jobs}
            conflictJobIds={conflictJobIds}
            onSlotClick={(date, startTime) =>
              handleOpenDrawer(undefined, { date, startTime })
            }
            onJobClick={(job) => handleOpenDrawer(job)}
            onDayClick={selectedDay ? undefined : setSelectedDay}
            onJobMove={canWrite && !isSaving ? handleMoveJob : undefined}
          />
        )}
        {layer === CalendarLayer.Trucks && (
          <ResourceWeekView
            days={days}
            today={today}
            rows={truckRows}
            resourceLabel="Truck"
            emptyMessage="No trucks found"
            conflictJobIds={conflictJobIds}
            onCellClick={(date, row) =>
              handleOpenDrawer(undefined, {
                ...getDayStartSlot(date),
                truckId: row.id,
              })
            }
            onJobClick={(job) => handleOpenDrawer(job)}
            onDayClick={selectedDay ? undefined : setSelectedDay}
          />
        )}
        {layer === CalendarLayer.Crew && (
          <ResourceWeekView
            days={days}
            today={today}
            rows={crewRows}
            resourceLabel="Crew Member"
            emptyMessage="No crew members found"
            conflictJobIds={conflictJobIds}
            onCellClick={(date, row) =>
              handleOpenDrawer(undefined, {
                ...getDayStartSlot(date),
                crewId: row.id,
              })
            }
            onJobClick={(job) => handleOpenDrawer(job)}
            onDayClick={selectedDay ? undefined : setSelectedDay}
          />
        )}
      </Box>

      <JobDrawer
        open={isDrawerOpen}
        job={selectedJob}
        slot={selectedSlot}
        jobs={jobs}
        accounts={accounts}
        accountDropdown={accountDropdown}
        trucks={trucks}
        crew={crew}
        canWrite={canWrite}
        canDelete={canDelete}
        isSaving={isSaving}
        onSave={handleSaveJob}
        onDelete={setJobToDelete}
        onClose={handleCloseDrawer}
      />

      <RecurrenceScopeDialog
        key={`edit-${pendingJob?.id}`}
        open={!!pendingJob}
        title="Edit repeating job"
        message="This job repeats. Which jobs should this change apply to?"
        options={RECURRENCE_EDIT_SCOPE_OPTIONS}
        primaryButtonText="Save"
        isSaving={isSaving}
        onClose={() => setPendingJob(undefined)}
        onConfirm={(scope) => pendingJob && updateJob(pendingJob, scope)}
      />

      <RecurrenceScopeDialog
        key={`delete-${jobToDelete?.id}`}
        open={!!jobToDelete && jobToDelete.seriesId !== null}
        title="Delete repeating job"
        message="This job repeats. Which jobs do you want to delete? Earlier jobs are not affected."
        options={RECURRENCE_SCOPE_OPTIONS}
        primaryButtonText="Delete"
        isSaving={isSaving}
        onClose={() => setJobToDelete(undefined)}
        onConfirm={deleteJob}
      />

      <ConfirmationModal
        open={!!jobToDelete && jobToDelete.seriesId === null}
        onClose={() => setJobToDelete(undefined)}
        onConfirm={() => deleteJob(RecurrenceEditScope.ThisOccurrence)}
        icon={
          <DeleteOutlineOutlinedIcon
            fontSize="medium"
            sx={{ color: theme.palette.error.main }}
          />
        }
        title={`Delete ${jobToDelete ? JobModel.getCategoryLabel(jobToDelete) : ""}`}
        message={`Delete this ${jobToDelete ? JobModel.getCategoryLabel(jobToDelete).toLowerCase() : ""}?`}
      />
    </Box>
  );
};
